import type {
  AlertWindowType,
  CompareWindow,
  ConditionJoin,
  ConditionOperator,
  ThresholdMode,
} from './alert.types'

export interface AlertLabelToken {
  labelKey: string
  labelParams?: Readonly<Record<string, number | string>>
}

export interface ConditionOption {
  operator: ConditionOperator
  legacyField: string
  valueLabelKey: string
  percentLabelKey: string
  absSymbol: string
  percentSymbol: string
  trend: 'up' | 'down'
}

export const CONDITION_OPTIONS: readonly ConditionOption[] = [
  {
    operator: '<',
    legacyField: 'less_than',
    valueLabelKey: 'alerts.operator.value.lessThan',
    percentLabelKey: 'alerts.operator.percent.decreaseMoreThan',
    absSymbol: '<',
    percentSymbol: '>',
    trend: 'down',
  },
  {
    operator: '<=',
    legacyField: 'less_than_or_equal_to',
    valueLabelKey: 'alerts.operator.value.lessOrEqual',
    percentLabelKey: 'alerts.operator.percent.decreaseAtLeast',
    absSymbol: '≤',
    percentSymbol: '≥',
    trend: 'down',
  },
  {
    operator: '>',
    legacyField: 'greater_than',
    valueLabelKey: 'alerts.operator.value.greaterThan',
    percentLabelKey: 'alerts.operator.percent.increaseMoreThan',
    absSymbol: '>',
    percentSymbol: '>',
    trend: 'up',
  },
  {
    operator: '>=',
    legacyField: 'greater_than_or_equal_to',
    valueLabelKey: 'alerts.operator.value.greaterOrEqual',
    percentLabelKey: 'alerts.operator.percent.increaseAtLeast',
    absSymbol: '≥',
    percentSymbol: '≥',
    trend: 'up',
  },
]

export const CONDITION_JOINS: readonly ConditionJoin[] = ['OR', 'AND']

export const CONDITION_JOIN_LABEL_KEYS = {
  OR: 'alerts.condition.join.or',
  AND: 'alerts.condition.join.and',
} as const satisfies Record<ConditionJoin, string>

export const CONDITION_JOIN_HINT_KEYS = {
  OR: 'alerts.condition.joinHint.or',
  AND: 'alerts.condition.joinHint.and',
} as const satisfies Record<ConditionJoin, string>

export function normalizeConditionJoin(value: unknown): ConditionJoin {
  if (typeof value !== 'string') return 'OR'
  const normalized = value.trim().toUpperCase()
  return CONDITION_JOINS.includes(normalized as ConditionJoin)
    ? (normalized as ConditionJoin)
    : 'OR'
}

export const COMPARE_WINDOWS_BY_WINDOW = {
  daily: ['previous_day', 'same_day_last_week', 'same_day_last_month'],
  weekly: ['previous_week', 'same_week_last_month'],
  custom: ['previous_n_days'],
} as const satisfies Record<AlertWindowType, readonly CompareWindow[]>

export const COMPARE_WINDOW_LABEL_KEYS = {
  previous_day: 'alerts.compare.previousDay',
  same_day_last_week: 'alerts.compare.sameDayLastWeek',
  same_day_last_month: 'alerts.compare.sameDayLastMonth',
  previous_week: 'alerts.compare.previous7Days',
  same_week_last_month: 'alerts.compare.sameWeekLastMonth',
  previous_n_days: 'alerts.compare.previousNDays',
} as const satisfies Record<CompareWindow, string>

const COMPARE_WINDOWS = new Set<CompareWindow>(Object.values(COMPARE_WINDOWS_BY_WINDOW).flat())

export function prettifyAlertValue(value: string): string {
  return value
    .replaceAll('_', ' ')
    .replace(/\s+/gu, ' ')
    .replace(/(^| )(\p{L})/gu, (_, prefix: string, character: string) => {
      return `${prefix}${character.toUpperCase()}`
    })
}

export function conditionOptionFor(value: unknown): ConditionOption | null {
  if (typeof value !== 'string') return null
  return (
    CONDITION_OPTIONS.find((option) => option.operator === value || option.legacyField === value) ??
    null
  )
}

export function conditionLabelFor(value: unknown, mode: ThresholdMode): AlertLabelToken {
  const option = conditionOptionFor(value)
  if (option) {
    return { labelKey: mode === 'percentage' ? option.percentLabelKey : option.valueLabelKey }
  }
  return typeof value === 'string' && value.trim()
    ? {
        labelKey: 'alerts.operator.unknown',
        labelParams: { value: prettifyAlertValue(value.trim()) },
      }
    : { labelKey: 'alerts.operator.unknown' }
}

/** Accept the mixed legacy corpus, but expose only the contracted snake_case vocabulary. */
export function normalizeCompareWindow(
  value: unknown,
  window?: AlertWindowType,
): CompareWindow | null {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toLowerCase().replace(/\s+/gu, '_')
  if (COMPARE_WINDOWS.has(normalized as CompareWindow)) return normalized as CompareWindow
  if (!/^previous_\d+_days$/u.test(normalized)) return null
  if (normalized === 'previous_7_days' && window !== 'custom') return 'previous_week'
  return 'previous_n_days'
}

export function compareWindowLabelFor(value: unknown): AlertLabelToken {
  const normalized = normalizeCompareWindow(value)
  if (normalized) return { labelKey: COMPARE_WINDOW_LABEL_KEYS[normalized] }
  return typeof value === 'string' && value.trim()
    ? {
        labelKey: 'alerts.compare.unknown',
        labelParams: { value: prettifyAlertValue(value.trim()) },
      }
    : { labelKey: 'alerts.compare.unknown' }
}

export const CUSTOM_WINDOW = { min: 2, max: 30 } as const
